export type ConfirmationFact = {
  code: string
  label: string
  value: string
  needsAttention: boolean
}

export type ConfirmationBriefGuidance = {
  facts: ConfirmationFact[]
  focusFacts: ConfirmationFact[]
  considerations: string[]
  hasConflictingConsequences: boolean
}

const questionLabels: Record<string, string> = {
  'R1-AS-01': 'Credible consequences',
  'R1-AS-02': 'Role in decisions about people',
  'R1-AS-03': 'Meaningful human decision review',
  'R1-AS-04': 'Correction and human review',
  'R1-AS-05': 'Scale of use',
  'R1-AS-06': 'Information involved',
  'R1-AS-07': 'Use of personal information',
  'R1-AS-08': 'Actions in another system',
  'R1-AS-09': 'Approval before action',
  'R1-AS-10': 'Intended-use testing',
  'R1-AS-11': 'Ability to correct an error',
  'R1-AS-12': 'Direct interaction with people',
  'R1-AS-13': 'Legal, regulatory or professional context',
  'R1-AS-14': 'Safety consequence',
  'R1-AS-15': 'Operational fallback',
}

const focusByType: Record<string, string[]> = {
  product: ['R1-AS-04', 'R1-AS-10', 'R1-AS-11', 'R1-AS-15'],
  technical: ['R1-AS-04', 'R1-AS-06', 'R1-AS-08', 'R1-AS-09', 'R1-AS-10', 'R1-AS-11', 'R1-AS-15'],
  security: ['R1-AS-06', 'R1-AS-08', 'R1-AS-09', 'R1-AS-10', 'R1-AS-11', 'R1-AS-15'],
  privacy: ['R1-AS-06', 'R1-AS-07', 'R1-AS-12'],
  legal_professional: ['R1-AS-01', 'R1-AS-02', 'R1-AS-03', 'R1-AS-08', 'R1-AS-09', 'R1-AS-13', 'R1-AS-14', 'R1-AS-15'],
}

const considerationsByType: Record<string, string[]> = {
  product: [
    'Confirm that the named product and configured capabilities match the way the organisation intends to use it.',
    'Check that known limitations, supported uses and supplier dependencies are accurately recorded.',
    'Confirm that realistic testing and a practical fallback are proportionate to the intended use.',
  ],
  technical: [
    'Confirm what the system can actually access, generate, change or trigger in its configured environment.',
    'Check permission boundaries, approval gates, logging and the ability to stop or reverse an action.',
    'Confirm that testing, correction and fallback arrangements reflect the real technical design.',
  ],
  security: [
    'Access: Who can access the system and its data? Are administrator access and permissions limited to people who need them?',
    'Credentials and secrets: Are passwords, API keys and other credentials stored securely and changed when required?',
    'Environment separation: Are development, testing and production separated, with production data and credentials kept out of lower environments?',
    'Logging: Are important actions recorded so Banksia can identify which person or system performed them?',
    'Containment: Can the integration be disabled quickly? Can incorrect or harmful actions be stopped, reversed or contained?',
    'Testing and fallback: Has the system been tested for this intended use, and can staff continue safely if it fails or becomes unavailable?',
  ],
  privacy: [
    'Confirm the purpose and authority for using personal, sensitive or confidential information.',
    'Check data minimisation, access, retention, disclosure and any different or secondary use of information.',
    'Consider transparency, individual rights and whether a privacy assessment or additional conditions are required.',
  ],
  legal_professional: [
    'Confirm whether the proposed use is authorised under applicable legal, regulatory, contractual or professional obligations.',
    'Consider whether meaningful human approval is required before consequential or financial actions occur.',
    'Check whether records, explanations, evidence or professional oversight must be retained.',
    'Confirm that testing, correction and fallback arrangements are adequate for the consequences identified.',
  ],
}

function object(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {}
}

function strings(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string')
    : []
}

function answerCodes(answer: Record<string, unknown>) {
  const values = strings(answer.answerValue)
  const code = typeof answer.answerCode === 'string' ? answer.answerCode : ''
  return values.length > 0 ? values : code ? [code] : []
}

function answerDisplay(answer: Record<string, unknown>) {
  if (typeof answer.answerDisplay === 'string' && answer.answerDisplay.trim()) return answer.answerDisplay.trim()
  const values = strings(answer.answerValue)
  if (values.length > 0) return values.join(', ').replaceAll('_', ' ')
  return typeof answer.answerCode === 'string' ? answer.answerCode.replaceAll('_', ' ') : 'Recorded'
}

function includesText(facts: ConfirmationFact[], pattern: RegExp) {
  return facts.some((fact) => pattern.test(fact.value))
}

export function buildConfirmationBriefGuidance(
  confirmationType: string,
  snapshot: unknown,
): ConfirmationBriefGuidance {
  const focusCodes = focusByType[confirmationType] ?? Object.keys(questionLabels)
  const facts = Object.entries(object(snapshot))
    .filter(([code]) => code.startsWith('R1-AS-'))
    .map(([code, value]) => {
      const answer = object(value)
      return {
        code,
        label: questionLabels[code] ?? 'Assessment fact',
        value: answerDisplay(answer),
        needsAttention: focusCodes.includes(code),
      }
    })
    .sort((left, right) => left.code.localeCompare(right.code, undefined, { numeric: true }))

  const consequence = object(object(snapshot)['R1-AS-01'])
  const consequenceCodes = answerCodes(consequence)
  const consequenceDisplay = answerDisplay(consequence)
  const hasMinor = consequenceCodes.includes('minor_easy_correct') || /minor and easy to correct/i.test(consequenceDisplay)
  const hasOtherConsequence = consequenceCodes.some((code) => code !== 'minor_easy_correct' && code !== 'unknown')
    || (hasMinor && /work may need|financial|legal|safety|disruption|exposure|unfair/i.test(consequenceDisplay))
  const hasConflictingConsequences = hasMinor && hasOtherConsequence

  const considerations = [...(considerationsByType[confirmationType] ?? [
    'Confirm that the recorded facts are accurate and sufficient for the specialist judgement requested.',
    'Identify any conditions, corrections or additional evidence required before the AI use proceeds.',
  ])]

  if (confirmationType !== 'security' && includesText(facts.filter((fact) => fact.code === 'R1-AS-08'), /payment|financial commitment/i)) {
    considerations.push('Confirm who is authorised to approve financial commitments and whether the AI can act without that approval.')
  }
  if (confirmationType !== 'security' && includesText(facts.filter((fact) => fact.code === 'R1-AS-06'), /source code|technical information/i)) {
    considerations.push('Confirm how source code or technical information is restricted, protected and prevented from unintended disclosure.')
  }
  if (confirmationType !== 'security' && includesText(facts.filter((fact) => fact.code === 'R1-AS-13'), /finance|credit|insurance/i)) {
    considerations.push('Confirm the specific finance, credit or insurance obligations and the qualified authority responsible for them.')
  }
  if (confirmationType !== 'security' && includesText(facts.filter((fact) => fact.code === 'R1-AS-15'), /material disruption/i)) {
    considerations.push('Confirm that the fallback can be activated in practice and that the expected disruption is acceptable.')
  }

  return {
    facts,
    focusFacts: facts.filter((fact) => fact.needsAttention),
    considerations: Array.from(new Set(considerations)),
    hasConflictingConsequences,
  }
}
